/**
 * EventMap / SmartAI-style scheduling for enemy abilities.
 * Timers live in `unit.ai.timers` as absolute due times (ms), so they snapshot with the World.
 */
import type { EnemyTimer, Ms, Rng } from '../contracts';
import { TICK_MS } from '../contracts';

/** SmartAI ranges ({initialMin..Max, repeatMin..Max}) are EnemyTimer.firstMax/everyMax. */
export type TimerRanges = EnemyTimer;

/** Trash without explicit jitter still gets a SmartAI-like spread of ±this fraction. */
export const TRASH_DEFAULT_SPREAD = 0.15;

/** Keys in `ai.timers` that are not abilities. */
export const ENRAGE_KEY = '@enrage';
export const isReservedKey = (k: string): boolean => k.startsWith('@');

/**
 * Roll one delay.
 * - `max` given: uniform integer in [base, max] (SmartAI range).
 * - otherwise `base ± jitter`; trash with no jitter uses ±TRASH_DEFAULT_SPREAD of base.
 */
export function rollDelay(rng: Rng, base: Ms, max: Ms | undefined, jitter: Ms | undefined, trash: boolean): Ms {
  let v: Ms;
  if (max !== undefined && max > base) v = rng.int(base, max);
  else {
    const j = jitter ?? (trash ? Math.round(base * TRASH_DEFAULT_SPREAD) : 0);
    v = j > 0 ? base + rng.int(-j, j) : base;
  }
  return Math.max(0, Math.round(v));
}

export const firstDelay = (rng: Rng, t: TimerRanges, trash: boolean): Ms => rollDelay(rng, t.first, t.firstMax, t.jitter, trash);

/** Repeat interval, counted from the moment the ability fires (EventMap `Repeat`). */
export const repeatDelay = (rng: Rng, t: TimerRanges, trash: boolean): Ms => Math.max(TICK_MS, rollDelay(rng, t.every, t.everyMax, t.jitter, trash));
